import { useCallback, useMemo } from "react";

import { useAudienceDispatch, useCompareContext } from "@/context";
import { useServices } from "@/hooks";
import { Audience } from "@/types";
import { audiencePath } from "@/utils";

/**
 * Navigation to and between audiences. Holds no state, so it's cheap to use
 * in every card and list row.
 */
export const useAudienceLinks = (audience?: Audience) => {
  const { nav } = useServices();
  const dispatch = useAudienceDispatch();
  const { dispatch: compareDispatch } = useCompareContext();

  // Relative, so it opens the new audience page of the current list
  const create = useCallback(() => {
    dispatch({ type: "CREATE_AUDIENCE" });
    nav.navigate("new");
  }, [dispatch, nav]);

  const goTo = useCallback((segment?: "insight" | "export") => {
    if (!audience) return;
    dispatch({ type: "GET_AUDIENCE", payload: { audience } });
    nav.navigate(audiencePath(audience, segment));
  }, [audience, dispatch, nav]);

  const open = useCallback(() => goTo(), [goTo]);
  const insight = useCallback(() => goTo("insight"), [goTo]);
  const exportAudience = useCallback(() => goTo("export"), [goTo]);

  const compare = useCallback(() => {
    if (!audience) return;
    compareDispatch({ type: "ADD_TO_COMPARE", payload: { audience } });
    nav.navigate("/compare", { state: { audience } });
  }, [audience, compareDispatch, nav]);

  return useMemo(
    () => ({ create, open, insight, export: exportAudience, compare }),
    [create, open, insight, exportAudience, compare]
  );
}

export type AudienceLinks = ReturnType<typeof useAudienceLinks>;
